// src/pages/MagazinePage.jsx
import React, { useState } from "react";
import { Btn, Modal, SectionHeader, EmptyState, InfoBanner, Input, Textarea, Select, FileUpload, ProgressBar } from "../components/UI";
import { uploadFile, deleteFile } from "../utils/uploadFile";
import { addDocument, deleteDocument } from "../hooks/useFirestore";

function MagazineForm({ onSave, onClose, categories }) {
  const [form, setForm] = useState({
    title: "", description: "", issue: "",
    date: new Date().toISOString().split("T")[0], category: "",
    thumbnailFile: null, thumbnailPreview: "",
    pdfFile: null, pdfName: "",
  });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Title is required."); return; }
    if (!form.pdfFile) { setError("Please upload a PDF file."); return; }
    setError(""); setUploading(true);

    try {
      let thumbnailURL = "";
      if (form.thumbnailFile) {
        thumbnailURL = await uploadFile(form.thumbnailFile, "magazines/thumbnails", p => setProgress(p * 0.4));
      }
      let pdfURL = "";
      if (form.pdfFile) {
        pdfURL = await uploadFile(form.pdfFile, "magazines/pdfs", p => setProgress(40 + p * 0.6));
      }
      setProgress(100);
      await addDocument("magazines", {
        title: form.title, description: form.description, issue: form.issue,
        date: form.date, category: form.category, thumbnailURL, pdfURL,
      });
      onSave();
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
        padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c53030" }}>{error}</div>}
      {uploading && <ProgressBar value={progress} label="Uploading..." />}

      <Input label="Magazine Title" value={form.title} onChange={v => set("title", v)} required placeholder="e.g. AgriPragati Monthly — January 2025" />
      <Textarea label="Description" value={form.description} onChange={v => set("description", v)} rows={3} placeholder="Short description of this issue..." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Input label="Issue Number" value={form.issue} onChange={v => set("issue", v)} placeholder="e.g. Vol. 3, Issue 2" />
        <Input label="Date" type="date" value={form.date} onChange={v => set("date", v)} />
      </div>
      <Select label="Category" value={form.category} onChange={v => set("category", v)} options={categories} />
      <FileUpload label="Cover Image / Thumbnail" accept="image/*"
        onFile={f => { set("thumbnailFile", f); set("thumbnailPreview", URL.createObjectURL(f)); }}
        preview={form.thumbnailPreview} hint="Magazine cover image — JPG, PNG" />
      <FileUpload label="Magazine PDF" accept=".pdf" required
        onFile={f => { set("pdfFile", f); set("pdfName", f.name); }}
        hint={form.pdfName ? `✓ ${form.pdfName}` : "Upload PDF file (required)"} />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 14, borderTop: "1px solid #eee" }}>
        <Btn onClick={onClose} variant="secondary" disabled={uploading}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={uploading}>📰 Upload Magazine</Btn>
      </div>
    </div>
  );
}

export default function MagazinePage({ magazines, categories, isAdmin }) {
  const [showForm, setShowForm] = useState(false);

  const handleDelete = async (mag) => {
    if (!window.confirm(`Delete "${mag.title}"?`)) return;
    try {
      if (mag.thumbnailURL) await deleteFile(mag.thumbnailURL);
      if (mag.pdfURL) await deleteFile(mag.pdfURL);
      await deleteDocument("magazines", mag.id);
    } catch (err) { alert("Delete failed."); }
  };

  return (
    <div>
      {!isAdmin && <InfoBanner type="warning">👁 View-only mode.</InfoBanner>}

      <SectionHeader
        title="Magazine"
        action={isAdmin && <Btn onClick={() => setShowForm(true)}>+ Upload Magazine</Btn>}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 16 }}>
        {magazines.map(m => (
          <div key={m.id} style={{ background: "#fff", border: "1px solid #e8f0e9", borderRadius: 12, overflow: "hidden" }}>
            {m.thumbnailURL ? (
              <img src={m.thumbnailURL} alt={m.title} style={{ width: "100%", height: 150, objectFit: "cover" }} />
            ) : (
              <div style={{ height: 150, background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>📰</div>
            )}
            <div style={{ padding: "12px 14px" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#1a1a1a", marginBottom: 4 }}>{m.title}</div>
              {m.description && <p style={{ fontSize: 12, color: "#666", margin: "0 0 6px", lineHeight: 1.4 }}>{m.description}</p>}
              <div style={{ fontSize: 11, color: "#999" }}>{m.issue && `${m.issue} · `}{m.date}</div>
              {m.pdfURL && (
                <a href={m.pdfURL} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-block", marginTop: 8, fontSize: 12, color: "#2d7a3a", fontWeight: 600 }}>
                  📄 View PDF
                </a>
              )}
              {isAdmin && (
                <div style={{ marginTop: 10 }}>
                  <Btn variant="danger" style={{ fontSize: 11, padding: "5px 12px", width: "100%" }}
                    onClick={() => handleDelete(m)}>🗑 Delete</Btn>
                </div>
              )}
            </div>
          </div>
        ))}
        {!magazines.length && (
          <div style={{ gridColumn: "1 / -1" }}>
            <EmptyState icon="📰" message={isAdmin ? "No magazines yet. Click '+ Upload Magazine' to add one." : "No magazines available."} />
          </div>
        )}
      </div>

      {showForm && (
        <Modal title="Upload New Magazine" onClose={() => setShowForm(false)}>
          <MagazineForm onSave={() => setShowForm(false)} onClose={() => setShowForm(false)} categories={categories} />
        </Modal>
      )}
    </div>
  );
}
